import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Bitcoin, Shield, CheckCircle2, ArrowRight, 
  MapPin, DollarSign, TrendingDown, AlertTriangle,
  Lock, Server, FileCheck, Users, Briefcase, Clock,
  ChevronRight, ExternalLink, BarChart3, Activity, Target
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Card } from '../components/ui/card';

const ExampleTransactionPage = () => {
  const navigate = useNavigate();
  const [btcPrice, setBtcPrice] = useState(100000);
  const [activePhase, setActivePhase] = useState(1);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Monte Carlo Parameters
  const [btcAmount, setBtcAmount] = useState(1.5);
  const [aggregateHaircut, setAggregateHaircut] = useState(55);
  const [btcExpectedReturn, setBtcExpectedReturn] = useState(25);
  const [btcVolatility, setBtcVolatility] = useState(65);
  const [propertyExpectedReturn, setPropertyExpectedReturn] = useState(8);
  const [defaultProbability, setDefaultProbability] = useState(10);
  const [numSimulations, setNumSimulations] = useState(1000);
  
  // Simulation Results
  const [isSimulating, setIsSimulating] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchBtcPrice();
  }, []);

  const fetchBtcPrice = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/crypto/prices`);
      const data = await response.json();
      if (data.success) {
        setBtcPrice(data.prices.BTC.price);
      }
    } catch (error) {
      console.error('Error fetching BTC price:', error);
    }
  };

  // Oxbridge Square Property Data
  const property = {
    name: 'Oxbridge Square',
    address: '10009 Hull St, Richmond, VA 23236',
    type: 'Retail Shopping Center',
    size: '116,240 sq ft',
    yearBuilt: '-',
    appraisedValue: 12900000,
    currentLoan: 6825000,
    interestRate: 3.39,
    maturityDate: 'February 1, 2030',
    occupancy: 68.04,
    underwritingOccupancy: 89.10,
    status: 'Watchlist',
    noi: 1059129,
    dealName: 'RCMF 2022-7',
    servicer: 'KeyBank',
    triggerTenant: 'Big Lots, Inc.',
    isDistressed: true
  };

  // Dynamic Transaction Calculation
  const btcMarketValue = btcAmount * btcPrice;
  const usableBtcCollateral = btcMarketValue * (1 - aggregateHaircut / 100);
  const reEquityCollateral = property.appraisedValue - property.currentLoan;
  const totalCollateral = usableBtcCollateral + reEquityCollateral;
  const coverageRatio = (totalCollateral / property.currentLoan) * 100;
  const excessCollateral = totalCollateral - property.currentLoan;

  // Monte Carlo Simulation
  const runMonteCarloSimulation = () => {
    setIsSimulating(true);
    
    setTimeout(() => {
      const simResults = performMonteCarloSimulation();
      setResults(simResults);
      setIsSimulating(false);
    }, 100);
  };

  const performMonteCarloSimulation = () => {
    let bankProfits = [];
    let coverageRatios = [];
    let defaultCount = 0;
    let bankLossCount = 0;
    let finalBtcPrices = [];
    const loanTermYears = 3;

    for (let i = 0; i < numSimulations; i++) {
      const defaulted = Math.random() < (defaultProbability / 100);
      
      // Simulate Bitcoin price
      const dt = loanTermYears;
      const drift = (btcExpectedReturn / 100) * dt;
      const randomShock = (btcVolatility / 100) * Math.sqrt(dt) * randomNormal();
      const btcReturn = Math.exp(drift + randomShock);
      const finalBtcPrice = btcPrice * btcReturn;
      finalBtcPrices.push(finalBtcPrice);
      
      // Simulate property value
      const propertyDrift = (propertyExpectedReturn / 100) * dt;
      const propertyShock = 0.20 * Math.sqrt(dt) * randomNormal();
      const propertyReturn = Math.exp(propertyDrift + propertyShock);
      const finalPropertyValue = property.appraisedValue * propertyReturn;

      // Calculate collateral at simulation end
      const finalBtcValue = btcAmount * finalBtcPrice;
      const finalUsableBtc = finalBtcValue * (1 - aggregateHaircut / 100);
      const finalReEquity = finalPropertyValue - property.currentLoan;
      const finalTotalCollateral = finalUsableBtc + Math.max(0, finalReEquity);
      const finalCoverage = (finalTotalCollateral / property.currentLoan) * 100;
      coverageRatios.push(finalCoverage);

      if (defaulted) {
        defaultCount++;
        const btcRecovery = btcAmount * finalBtcPrice;
        const propertyRecovery = finalPropertyValue * 0.85;
        const totalRecovery = btcRecovery + propertyRecovery;
        const bankProfit = totalRecovery - property.currentLoan - (property.currentLoan * 0.05);
        bankProfits.push(bankProfit);
        if (bankProfit < 0) bankLossCount++;
      } else {
        const interestEarned = property.currentLoan * (property.interestRate / 100) * loanTermYears;
        bankProfits.push(interestEarned);
      }
    }

    const bankMeanProfit = mean(bankProfits);
    const meanBtcPrice = mean(finalBtcPrices);
    const btcPriceChange = ((meanBtcPrice - btcPrice) / btcPrice) * 100;
    const meanCoverageRatio = mean(coverageRatios);

    return {
      bank: {
        meanProfit: bankMeanProfit,
        medianProfit: median(bankProfits),
        percentile5: percentile(bankProfits, 5),
        percentile95: percentile(bankProfits, 95),
        lossProbability: (bankLossCount / numSimulations) * 100,
        roi: (bankMeanProfit / property.currentLoan) * 100
      },
      defaultRate: (defaultCount / numSimulations) * 100,
      meanBtcPrice,
      btcPriceChange,
      meanCoverageRatio,
      minCoverage: Math.min(...coverageRatios),
      maxCoverage: Math.max(...coverageRatios)
    };
  };

  const randomNormal = () => {
    let u = 0, v = 0;
    while(u === 0) u = Math.random();
    while(v === 0) v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  };

  const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const median = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  const percentile = (arr, p) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const index = Math.floor((p / 100) * sorted.length);
    return sorted[index];
  };

  const fmt = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}K`;

  const partnershipPhases = [
    {
      phase: 1,
      title: 'Technical Review',
      duration: 'Weeks 1–3',
      items: [
        'API documentation & integration assessment',
        'Security audit & penetration testing review',
        'Compliance framework evaluation with legal team',
        'Custody provider due diligence'
      ],
      outcome: 'Feasibility confirmed + integration scope defined'
    },
    {
      phase: 2,
      title: 'Pilot Transactions (1–3 Loans)',
      duration: 'Days 30–90',
      items: [
        'Live transactions with full documentation review',
        'Credit committee presentation & approval',
        'SAR procedure + compliance validation',
        'Loan officer & underwriter training'
      ],
      outcome: 'Risk assessment complete + team trained'
    },
    {
      phase: 3,
      title: 'Production Launch',
      duration: 'Day 90+',
      items: [
        'Full platform go-live & marketing materials',
        'Broker & correspondent network education',
        'Dedicated AssumeRate support team assigned',
        'Quarterly business reviews & optimization'
      ],
      outcome: 'Live revenue-generating program'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-8 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate('/how-it-works')}
            variant="outline"
            className="mb-6 border-[#006AFF] text-[#006AFF]"
          >
            ← Back to Process Flow
          </Button>
          
          <div className="text-center">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Real Distressed Property Example
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2A2A33] mb-4">
              Example Transaction: <span className="text-[#006AFF]">{property.name}</span>
            </h1>
            <p className="text-lg text-[#6B6B76] max-w-3xl mx-auto">
              Interactive Monte Carlo simulation - adjust parameters and see real-time risk analysis
            </p>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="px-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4">
            {[
              { id: 'overview', label: 'Deal Overview' },
              { id: 'simulation', label: 'Monte Carlo Simulation' },
              { id: 'partnership', label: 'Partnership Program' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#006AFF] text-[#006AFF]'
                    : 'border-transparent text-[#6B6B76] hover:text-[#2A2A33]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Deal Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Property Details Card */}
          <section className="py-8 px-4 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="bg-[#F7F7F7] rounded-2xl p-8 border border-gray-200">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="text-sm text-[#006AFF] font-medium mb-1">{property.type.toUpperCase()}</div>
                        <h2 className="text-2xl font-bold text-[#2A2A33]">{property.name}</h2>
                      </div>
                      <span className="bg-red-100 text-red-600 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                        <TrendingDown className="h-4 w-4 mr-1" />
                        {property.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-[#6B6B76] mb-6">
                      <MapPin className="h-5 w-5 mr-2" />
                      {property.address}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl p-4">
                        <div className="text-sm text-[#6B6B76]">Property Size</div>
                        <div className="text-xl font-bold text-[#2A2A33]">{property.size}</div>
                      </div>
                      <div className="bg-white rounded-xl p-4">
                        <div className="text-sm text-[#6B6B76]">Current Occupancy</div>
                        <div className="text-xl font-bold text-red-500">{property.occupancy}%</div>
                      </div>
                    </div>

                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                      <div className="text-sm font-semibold text-red-600 mb-1">Trigger Event</div>
                      <p className="text-sm text-red-700">
                        Cash Management triggered due to bankruptcy: <strong>{property.triggerTenant}</strong>
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Financial Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between p-3 bg-white rounded-lg">
                        <span className="text-[#6B6B76]">Appraised Value</span>
                        <span className="font-bold text-[#2A2A33]">${property.appraisedValue.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-white rounded-lg">
                        <span className="text-[#6B6B76]">Current Loan Balance</span>
                        <span className="font-bold text-[#2A2A33]">${property.currentLoan.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-white rounded-lg">
                        <span className="text-[#6B6B76]">Annual NOI</span>
                        <span className="font-bold text-[#2A2A33]">${property.noi.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between p-3 bg-[#E8F4FD] rounded-lg border border-[#006AFF]/20">
                        <span className="text-[#006AFF] font-medium">Real Estate Equity</span>
                        <span className="font-bold text-[#006AFF]">${reEquityCollateral.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Interactive Collateral Calculator */}
          <section className="py-12 px-4 bg-[#F7F7F7]">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-[#2A2A33] mb-2">
                  Interactive Credit Committee View
                </h2>
                <p className="text-[#6B6B76]">Adjust the sliders to see how collateral coverage changes</p>
              </div>

              <div className="grid md:grid-cols-4 gap-6">
                {/* Sliders */}
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="font-bold text-[#2A2A33] mb-4">Adjust Parameters</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">BTC Amount</label>
                        <span className="text-sm font-semibold text-[#006AFF]">₿ {btcAmount.toFixed(1)}</span>
                      </div>
                      <Slider value={[btcAmount]} onValueChange={(val) => setBtcAmount(val[0])} min={0.5} max={5} step={0.1} />
                      <div className="text-xs text-[#6B6B76] mt-1">${btcMarketValue.toLocaleString()}</div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">BTC Haircut</label>
                        <span className="text-sm font-semibold text-[#2A2A33]">{aggregateHaircut}%</span>
                      </div>
                      <Slider value={[aggregateHaircut]} onValueChange={(val) => setAggregateHaircut(val[0])} min={30} max={70} step={5} />
                    </div>

                    <div className="pt-3 border-t border-gray-200">
                      <div className="text-xs text-[#6B6B76]">Live BTC Price</div>
                      <div className="text-lg font-bold text-[#006AFF]">${btcPrice.toLocaleString()}</div>
                    </div>
                  </div>
                </Card>

                {/* Bitcoin Collateral */}
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-[#FEF3C7] rounded-lg flex items-center justify-center mr-3">
                      <Bitcoin className="h-5 w-5 text-[#F59E0B]" />
                    </div>
                    <h3 className="font-bold text-[#2A2A33]">BTC Collateral</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B76]">Market Value</span>
                      <span className="font-semibold">${btcMarketValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm text-red-500">
                      <span>Haircut ({aggregateHaircut}%)</span>
                      <span>−${(btcMarketValue * aggregateHaircut / 100).toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-medium">Usable</span>
                      <span className="font-bold text-[#F59E0B]">${usableBtcCollateral.toLocaleString()}</span>
                    </div>
                  </div>
                </Card>

                {/* RE Collateral */}
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-[#E8F4FD] rounded-lg flex items-center justify-center mr-3">
                      <Building2 className="h-5 w-5 text-[#006AFF]" />
                    </div>
                    <h3 className="font-bold text-[#2A2A33]">RE Collateral</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#6B6B76]">Appraised Value</span>
                      <span className="font-semibold">${property.appraisedValue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm text-red-500">
                      <span>Loan Balance</span>
                      <span>−${property.currentLoan.toLocaleString()}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-medium">RE Equity</span>
                      <span className="font-bold text-[#006AFF]">${reEquityCollateral.toLocaleString()}</span>
                    </div>
                  </div>
                </Card>

                {/* Combined */}
                <Card className="bg-[#006AFF] text-white p-6 shadow-sm">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                      <Shield className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-bold">Combined</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-blue-100">Total Collateral</span>
                      <span className="font-bold text-xl">${totalCollateral.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-100">Loan Amount</span>
                      <span>${property.currentLoan.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/20 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-100">Coverage</span>
                        <span className={`font-bold text-2xl ${coverageRatio >= 100 ? 'text-green-300' : 'text-red-300'}`}>
                          {coverageRatio.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-blue-100 text-sm">Excess</span>
                        <span className={`font-semibold ${excessCollateral >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          ${excessCollateral.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Monte Carlo Simulation Tab */}
      {activeTab === 'simulation' && (
        <section className="py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#2A2A33] mb-2">Monte Carlo Risk Simulation</h2>
              <p className="text-[#6B6B76]">
                Run {numSimulations.toLocaleString()} scenarios to stress-test the {property.name} deal
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Parameters */}
              <div className="space-y-6">
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Deal Parameters</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">BTC Collateral Amount</label>
                        <span className="text-sm font-semibold text-[#006AFF]">₿ {btcAmount.toFixed(1)}</span>
                      </div>
                      <Slider value={[btcAmount]} onValueChange={(val) => setBtcAmount(val[0])} min={0.5} max={10} step={0.5} />
                      <div className="text-xs text-[#6B6B76] mt-1">${btcMarketValue.toLocaleString()} at current price</div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">Aggregate Haircut</label>
                        <span className="text-sm font-semibold text-[#2A2A33]">{aggregateHaircut}%</span>
                      </div>
                      <Slider value={[aggregateHaircut]} onValueChange={(val) => setAggregateHaircut(val[0])} min={30} max={70} step={5} />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">Default Probability</label>
                        <span className={`text-sm font-semibold ${defaultProbability > 20 ? 'text-red-600' : 'text-[#2A2A33]'}`}>{defaultProbability}%</span>
                      </div>
                      <Slider value={[defaultProbability]} onValueChange={(val) => setDefaultProbability(val[0])} min={0} max={50} step={1} />
                    </div>
                  </div>
                </Card>

                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Market Assumptions</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">BTC Expected Return (Annual)</label>
                        <span className={`text-sm font-semibold ${btcExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{btcExpectedReturn}%</span>
                      </div>
                      <Slider value={[btcExpectedReturn]} onValueChange={(val) => setBtcExpectedReturn(val[0])} min={-50} max={100} step={5} />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">BTC Volatility</label>
                        <span className="text-sm font-semibold text-[#2A2A33]">{btcVolatility}%</span>
                      </div>
                      <Slider value={[btcVolatility]} onValueChange={(val) => setBtcVolatility(val[0])} min={20} max={120} step={5} />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">Property Expected Return</label>
                        <span className={`text-sm font-semibold ${propertyExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{propertyExpectedReturn}%</span>
                      </div>
                      <Slider value={[propertyExpectedReturn]} onValueChange={(val) => setPropertyExpectedReturn(val[0])} min={-30} max={30} step={1} />
                    </div>

                    <div>
                      <div className="flex justify-between mb-2">
                        <label className="text-sm text-[#6B6B76]">Number of Simulations</label>
                        <span className="text-sm font-semibold text-[#2A2A33]">{numSimulations.toLocaleString()}</span>
                      </div>
                      <Slider value={[numSimulations]} onValueChange={(val) => setNumSimulations(val[0])} min={100} max={5000} step={100} />
                    </div>
                  </div>
                </Card>

                <Button 
                  onClick={runMonteCarloSimulation}
                  disabled={isSimulating}
                  className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white py-6 text-lg"
                  data-testid="run-monte-carlo-btn"
                >
                  {isSimulating ? (
                    <><Activity className="mr-2 h-5 w-5 animate-spin" /> Running {numSimulations.toLocaleString()} Simulations...</>
                  ) : (
                    <><BarChart3 className="mr-2 h-5 w-5" /> Run Monte Carlo Simulation</>
                  )}
                </Button>
              </div>

              {/* Results */}
              <div className="space-y-6">
                {results ? (
                  <>
                    <Card className="bg-[#006AFF] text-white p-6 shadow-sm">
                      <div className="flex items-center mb-4">
                        <Shield className="h-6 w-6 mr-2" />
                        <h3 className="text-lg font-bold">Simulation Summary</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-3xl font-bold">{results.meanCoverageRatio.toFixed(0)}%</div>
                          <div className="text-sm text-blue-100">Mean Coverage</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold">{results.defaultRate.toFixed(1)}%</div>
                          <div className="text-sm text-blue-100">Default Rate</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-3xl font-bold ${results.btcPriceChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                            {results.btcPriceChange >= 0 ? '+' : ''}{results.btcPriceChange.toFixed(1)}%
                          </div>
                          <div className="text-sm text-blue-100">Avg BTC Δ</div>
                        </div>
                      </div>
                    </Card>

                    <Card className="bg-white border-gray-200 p-6 shadow-sm">
                      <h3 className="text-lg font-bold text-[#2A2A33] mb-4 flex items-center">
                        <DollarSign className="mr-2 h-5 w-5 text-green-600" />
                        Bank Performance
                      </h3>
                      
                      <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="text-sm text-[#6B6B76]">Mean Profit</div>
                          <div className="text-3xl font-bold text-green-600">{fmt(results.bank.meanProfit)}</div>
                          <div className="text-sm text-green-600">ROI: {results.bank.roi.toFixed(1)}%</div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-[#F7F7F7] rounded-lg p-3">
                            <div className="text-xs text-[#6B6B76]">5th Percentile (Worst)</div>
                            <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile5)}</div>
                          </div>
                          <div className="bg-[#F7F7F7] rounded-lg p-3">
                            <div className="text-xs text-[#6B6B76]">95th Percentile (Best)</div>
                            <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile95)}</div>
                          </div>
                        </div>

                        <div className={`rounded-lg p-4 ${results.bank.lossProbability < 5 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                          <div className="text-sm text-[#6B6B76]">Bank Loss Probability</div>
                          <div className={`text-2xl font-bold ${results.bank.lossProbability < 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                            {results.bank.lossProbability.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    </Card>

                    <Card className="bg-white border-gray-200 p-6 shadow-sm">
                      <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Coverage Distribution</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-[#6B6B76]">Minimum Coverage</span>
                          <span className={`font-semibold ${results.minCoverage >= 100 ? 'text-green-600' : 'text-red-600'}`}>
                            {results.minCoverage.toFixed(0)}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6B6B76]">Mean Coverage</span>
                          <span className="font-semibold text-[#006AFF]">{results.meanCoverageRatio.toFixed(0)}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6B6B76]">Maximum Coverage</span>
                          <span className="font-semibold text-green-600">{results.maxCoverage.toFixed(0)}%</span>
                        </div>
                        <div className="flex justify-between pt-3 border-t">
                          <span className="text-[#6B6B76]">Mean BTC Price (End)</span>
                          <span className="font-semibold text-[#2A2A33]">${results.meanBtcPrice.toLocaleString()}</span>
                        </div>
                      </div>
                    </Card>
                  </>
                ) : (
                  <Card className="bg-white border-gray-200 p-12 text-center shadow-sm">
                    <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-[#6B6B76] text-lg mb-2">No simulation results yet</p>
                    <p className="text-[#9B9BA5] text-sm">Adjust parameters and click "Run Monte Carlo Simulation" to see risk analysis</p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Partnership Program Tab */}
      {activeTab === 'partnership' && (
        <>
          {/* Bottom Line Benefits */}
          <section className="py-12 px-4 bg-white">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold text-[#2A2A33] text-center mb-12">
                What This Means for Your Bottom Line
              </h2>

              <div className="grid md:grid-cols-4 gap-6 mb-12">
                {[
                  { value: '154%', label: 'Avg Collateral Coverage', sublabel: 'vs. 80% LTV traditional' },
                  { value: '60%', label: 'Faster Workout', sublabel: '45–60 days vs. 120+ days' },
                  { value: '87%', label: 'Margin Call Cure Rate', sublabel: 'Without liquidation event' },
                  { value: '3%', label: 'Target Default Rate', sublabel: 'With dual-collateral protection' }
                ].map((stat, idx) => (
                  <Card key={idx} className="bg-[#F7F7F7] border-gray-200 p-6 text-center">
                    <div className="text-4xl font-bold text-[#006AFF] mb-2">{stat.value}</div>
                    <div className="font-medium text-[#2A2A33]">{stat.label}</div>
                    <div className="text-sm text-[#6B6B76]">{stat.sublabel}</div>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Partnership Program */}
          <section className="py-12 px-4 bg-[#F7F7F7]">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#2A2A33] mb-4">
                  Proof of Concept Partnership Program
                </h2>
                <p className="text-lg text-[#6B6B76]">
                  Zero risk. Full control. See real results before any commitment.
                </p>
              </div>

              <div className="flex justify-center gap-4 mb-8">
                {partnershipPhases.map((phase) => (
                  <button
                    key={phase.phase}
                    onClick={() => setActivePhase(phase.phase)}
                    className={`px-6 py-3 rounded-xl font-medium transition-all ${
                      activePhase === phase.phase
                        ? 'bg-[#006AFF] text-white shadow-lg'
                        : 'bg-white text-[#2A2A33] hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    Phase {phase.phase}
                  </button>
                ))}
              </div>

              {partnershipPhases.map((phase) => (
                phase.phase === activePhase && (
                  <Card key={phase.phase} className="bg-white border-gray-200 p-8 max-w-3xl mx-auto">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="text-sm text-[#006AFF] font-medium">Phase {phase.phase}</div>
                        <h3 className="text-2xl font-bold text-[#2A2A33]">{phase.title}</h3>
                      </div>
                      <div className="bg-[#E8F4FD] text-[#006AFF] px-4 py-2 rounded-full font-medium">
                        <Clock className="h-4 w-4 inline mr-2" />
                        {phase.duration}
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {phase.items.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#006AFF] mr-3">▸</span>
                          <span className="text-[#6B6B76]">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <span className="text-green-600 font-medium">→ {phase.outcome}</span>
                    </div>
                  </Card>
                )
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 px-4 bg-[#006AFF]">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Let's Build Something Together
              </h2>
              <p className="text-blue-100 mb-8">
                AssumeRate is actively selecting 3–5 launch partners for our Proof of Concept program.
              </p>
              
              <Button 
                onClick={() => window.location.href = 'mailto:contact@assumerate.com'}
                className="bg-white text-[#006AFF] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-lg font-semibold"
              >
                Contact Us: contact@assumerate.com
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
};

export default ExampleTransactionPage;
